import { logger } from "../../core/logging/logger";
import type { ConnectedServiceDomainApiService } from "../../core/net/connected-service-domain-api-service";
import type { Collection } from "../../utils/collection";
import type { HateoasLink } from "../../utils/hateoas-link";
import type { Keyboard, KeyboardRequest } from "../pincode/keyboard";
import type { PincodeSubmission } from "../pincode/pincode";
import type {
  PartyAuthenticationSession,
  PartyAuthenticationSessionLinks,
  PartyAuthenticationSessionStatus,
} from "./party-authentication-session";

export interface PartyAuthenticationSessionStatusUpdate {
  status: PartyAuthenticationSessionStatus;
  links: PartyAuthenticationSessionLinks;
}

export class PartyAuthenticationService {
  public constructor(private readonly apiService: ConnectedServiceDomainApiService) {}

  public async list(): Promise<PartyAuthenticationSession[]> {
    try {
      const response = await this.apiService
        .getInstance()
        .get<Collection<PartyAuthenticationSession>>("/authentications/sessions", {
          params: {
            sort: "-expirationDate",
          },
        });

      return response.data.items.map(this.getPartyAuthenticationSessionFromData, this);
    } catch (error) {
      logger.debug("PartyAuthenticationService", "Get party authentication sessions list failed", error);
      throw error;
    }
  }

  public async getSession(token: string): Promise<PartyAuthenticationSession> {
    try {
      const response = await this.apiService
        .getInstance()
        .get<PartyAuthenticationSession>(`/authentications/sessions/${token}`);

      return this.getPartyAuthenticationSessionFromData(response.data);
    } catch (e) {
      logger.debug("PartyAuthenticationService", `Get party authentication session #${token} failed`, e);
      throw e;
    }
  }

  public async authenticationSessionRefuse(token: string): Promise<void> {
    try {
      await this.apiService.getInstance().post(`/authentications/sessions/${token}/refuse`);

      return;
    } catch (e) {
      logger.debug("PartyAuthenticationService", `Refuse party authentication session #${token} failed`, e);
      throw new Error(e);
    }
  }

  public async authenticationSessionMarkSuspicious(token: string): Promise<void> {
    try {
      await this.apiService.getInstance().patch(`/authentications/sessions/${token}/mark-suspicious`);

      return;
    } catch (e) {
      logger.debug("PartyAuthenticationService", `Mark Suspicious party authentication session #${token} failed`, e);
      throw new Error(e);
    }
  }

  public async authenticationSessionCancel(link: HateoasLink): Promise<void> {
    return this.emptyAction(link);
  }

  public async authenticationSessionSendSMSOTP(link: HateoasLink): Promise<PartyAuthenticationSessionStatusUpdate> {
    return this.actionWithSessionStatus(link, {});
  }

  public async authenticationSessionVerifySMSOTP(
    link: HateoasLink,
    code: string,
  ): Promise<PartyAuthenticationSessionStatusUpdate> {
    return this.actionWithSessionStatus(link, { code });
  }

  public async generateVirtualKeyboard(token: string, keyboardSpec: KeyboardRequest): Promise<Keyboard> {
    try {
      const response = await this.apiService
        .getInstance()
        .post<Keyboard>(`/authentications/sessions/${token}/keyboard`, keyboardSpec);
      return response.data;
    } catch (e) {
      logger.debug("PartyAuthenticationService", `#${token} keyboard fetch failed`, e);
      throw e;
    }
  }

  public async authenticationSessionVerifyPincode(
    link: HateoasLink,
    pincode: PincodeSubmission,
  ): Promise<PartyAuthenticationSessionStatusUpdate> {
    return this.actionWithSessionStatus(link, { pincode });
  }

  public async authenticationSessionVerifyTrustedDevice(
    link: HateoasLink,
    secret: string,
  ): Promise<PartyAuthenticationSessionStatusUpdate> {
    return this.actionWithSessionStatus(link, { secret });
  }

  private async actionWithSessionStatus(link: HateoasLink, payload): Promise<PartyAuthenticationSessionStatusUpdate> {
    try {
      const href = link.href;

      const response = await this.apiService.getInstance().post<PartyAuthenticationSessionStatusUpdate>(href, payload);

      if (response.data?.status === "FAILED") {
        throw new Error("AUTHENTICATION_IS_ENDED");
      }

      return {
        ...response.data,
        // @ts-ignore
        links: this.extractLinks(response.data.links ?? []),
      };
    } catch (e) {
      logger.debug("PartyAuthenticationService", `Action failed with link.rel ${link.rel}`, e);

      if (e.response?.data?.status) {
        throw new Error({
          ...e.response.data,
          // @ts-ignore
          links: this.extractLinks(e.response.data.links ?? []),
        });
      }

      throw e;
    }
  }

  private async emptyAction(link: HateoasLink) {
    try {
      const href = link.href;

      await this.apiService.getInstance().post(href);
      return;
    } catch (e) {
      logger.debug("PartyAuthenticationService", `Action ${link.rel} failed on party authentication session failed`, e);
      throw e;
    }
  }

  private getPartyAuthenticationSessionFromData(item): PartyAuthenticationSession {
    return {
      ...item,
      initiatedAt: item.initiatedAt ? new Date(item.initiatedAt) : undefined,
      expiredAt: item.expiredAt ? new Date(item.expiredAt) : undefined,
      links: this.extractLinks(item.links),
    };
  }

  public extractLinks(links: Array<HateoasLink>): PartyAuthenticationSessionLinks {
    const sessionLinks: PartyAuthenticationSessionLinks = {};

    links.forEach((link) => {
      sessionLinks[link.rel] = link;
    });

    return sessionLinks;
  }
}
